import api from './api';
import { ENDPOINTS } from '../utils/constants';
import { formatApiError, logError } from '../utils/errorHandler';

/**
 * Customer/Company service functions for employee management and profile updates
 */

// ==================== Address Management Functions ====================

export const getAddresses = async (companyId) => {
  try {
    const response = await api.get(`${ENDPOINTS.ADDRESSES.LIST}?company=${companyId}`);
    return response.data;
  } catch (error) {
    logError('Get addresses', error);
    throw formatApiError(error);
  }
};

export const createAddress = async (addressData) => {
  try {
    const response = await api.post(ENDPOINTS.ADDRESSES.LIST, addressData);
    return response.data;
  } catch (error) {
    logError('Create address', error);
    throw formatApiError(error);
  }
};

export const updateAddress = async (addressId, addressData) => {
  try {
    const response = await api.patch(ENDPOINTS.ADDRESSES.DETAIL(addressId), addressData);
    return response.data;
  } catch (error) {
    logError('Update address', error);
    throw formatApiError(error);
  }
};

export const deleteAddress = async (addressId) => {
  try {
    await api.delete(ENDPOINTS.ADDRESSES.DETAIL(addressId));
  } catch (error) {
    logError('Delete address', error);
    throw formatApiError(error);
  }
};

export const setDefaultAddress = async (addressId) => {
  try {
    const response = await api.post(ENDPOINTS.ADDRESSES.SET_DEFAULT(addressId));
    return response.data;
  } catch (error) {
    logError('Set default address', error);
    throw formatApiError(error);
  }
};

// ==================== Note Management Functions ====================

export const getCompanyNotes = async (companyId) => {
  try {
    const response = await api.get(`${ENDPOINTS.NOTES.LIST}?company=${companyId}`);
    return response.data;
  } catch (error) {
    logError('Get notes', error);
    throw formatApiError(error);
  }
};

export const createNote = async (noteData) => {
  try {
    const response = await api.post(ENDPOINTS.NOTES.LIST, noteData);
    return response.data;
  } catch (error) {
    logError('Create note', error);
    throw formatApiError(error);
  }
};

export const updateNote = async (noteId, body) => {
  try {
    const response = await api.patch(ENDPOINTS.NOTES.DETAIL(noteId), { body });
    return response.data;
  } catch (error) {
    logError('Update note', error);
    throw formatApiError(error);
  }
};

export const deleteNote = async (noteId) => {
  try {
    await api.delete(ENDPOINTS.NOTES.DETAIL(noteId));
  } catch (error) {
    logError('Delete note', error);
    throw formatApiError(error);
  }
};

// ==================== Employee Company Management Functions ====================

/**
 * Get all companies (employee only)
 * @param {Object} filters - Optional filters
 * @param {string} filters.search - Search by company name
 * @returns {Promise<Object>} - Paginated companies response
 * @throws {Object} - Formatted error object
 */
export const getCustomers = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.page && filters.page > 1) params.append('page', filters.page);

    const queryString = params.toString();
    const url = queryString ? `${ENDPOINTS.COMPANIES.LIST}?${queryString}` : ENDPOINTS.COMPANIES.LIST;

    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logError('Get customers', error);
    throw formatApiError(error);
  }
};

/**
 * Get a single company by ID (employee only)
 * @param {number} companyId - Company ID
 * @returns {Promise<Object>} - Company object with details
 * @throws {Object} - Formatted error object
 */
export const getCustomer = async (companyId) => {
  if (!companyId || typeof companyId !== 'number') {
    throw formatApiError({ message: 'Invalid company ID' });
  }

  try {
    const response = await api.get(ENDPOINTS.COMPANIES.DETAIL(companyId));
    return response.data;
  } catch (error) {
    logError('Get customer', error);
    throw formatApiError(error);
  }
};

/**
 * Archive a company user (employee only) — soft-removes their access to the
 * account. Blocks login and drops them out of the active member list; their
 * orders and company link are untouched. Reversible via unarchiveCompanyUser.
 * @param {number} profileId - Profile ID
 * @param {string} [reason] - Optional free-text reason, stored for audit
 * @returns {Promise<Object>}
 * @throws {Object} - Formatted error object
 */
export const archiveCompanyUser = async (profileId, reason = '') => {
  try {
    const response = await api.post(ENDPOINTS.AUTH.ARCHIVE_ACCOUNT(profileId), { reason });
    return response.data;
  } catch (error) {
    logError('Archive company user', error);
    throw formatApiError(error);
  }
};

/**
 * Unarchive a company user (employee only) — restores login and re-lists
 * them as an active company member.
 * @param {number} profileId - Profile ID
 * @returns {Promise<Object>}
 * @throws {Object} - Formatted error object
 */
export const unarchiveCompanyUser = async (profileId) => {
  try {
    const response = await api.post(ENDPOINTS.AUTH.UNARCHIVE_ACCOUNT(profileId));
    return response.data;
  } catch (error) {
    logError('Unarchive company user', error);
    throw formatApiError(error);
  }
};

/**
 * Get all orders for a specific company (employee only)
 * @param {number} companyId - Company ID
 * @returns {Promise<Object>} - Paginated orders response
 * @throws {Object} - Formatted error object
 */
export const getCustomerOrders = async (companyId) => {
  if (!companyId || typeof companyId !== 'number') {
    throw formatApiError({ message: 'Invalid company ID' });
  }

  try {
    const response = await api.get(ENDPOINTS.COMPANIES.ORDERS(companyId));
    return response.data;
  } catch (error) {
    logError('Get customer orders', error);
    throw formatApiError(error);
  }
};

/**
 * Get all users/profiles for a specific company (employee only)
 * @param {number} companyId - Company ID
 * @param {Object} [options]
 * @param {boolean} [options.includeArchived] - Include archived members (default: active only)
 * @returns {Promise<Object>} - Paginated users response
 * @throws {Object} - Formatted error object
 */
export const getCustomerUsers = async (companyId, { includeArchived = false } = {}) => {
  if (!companyId || typeof companyId !== 'number') {
    throw formatApiError({ message: 'Invalid company ID' });
  }

  try {
    const url = includeArchived
      ? `${ENDPOINTS.COMPANIES.USERS(companyId)}?include_archived=true`
      : ENDPOINTS.COMPANIES.USERS(companyId);
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    logError('Get customer users', error);
    throw formatApiError(error);
  }
};

/**
 * Create a new company and optionally an associated user (employee only)
 * @param {Object} data - { company_name, phone, delivery_region, first_name, last_name, email }
 * @returns {Promise<Object>} - Created company object
 */
export const createCustomer = async (data) => {
  try {
    const response = await api.post(ENDPOINTS.COMPANIES.LIST, data);
    return response.data;
  } catch (error) {
    logError('Create customer', error);
    throw formatApiError(error);
  }
};

/**
 * Update a company's editable fields (employee only)
 * @param {number} companyId - Company ID
 * @param {Object} data - Fields to update (company_name, phone, delivery_region)
 * @returns {Promise<Object>} - Updated company object
 * @throws {Object} - Formatted error object
 */
export const updateCustomer = async (companyId, data) => {
  if (!companyId || typeof companyId !== 'number') {
    throw formatApiError({ message: 'Invalid company ID' });
  }

  try {
    const response = await api.patch(ENDPOINTS.COMPANIES.DETAIL(companyId), data);
    return response.data;
  } catch (error) {
    logError('Update customer', error);
    throw formatApiError(error);
  }
};

// ==================== Profile Management Functions ====================

/**
 * Get current user's profile
 * @returns {Promise<Object>} - Profile object with user and company data
 * @throws {Object} - Formatted error object
 */
export const getProfile = async () => {
  try {
    const response = await api.get(ENDPOINTS.AUTH.PROFILE);
    return response.data;
  } catch (error) {
    logError('Get profile', error);
    throw formatApiError(error);
  }
};

/**
 * Update current user's profile
 * @param {Object} profileData - Profile update data
 * @param {string} profileData.first_name - User first name
 * @param {string} profileData.last_name - User last name
 * @returns {Promise<Object>} - Updated profile object
 * @throws {Object} - Formatted error object
 */
export const updateProfile = async (profileData) => {
  if (!profileData || typeof profileData !== 'object') {
    throw { message: 'Invalid profile data' };
  }

  try {
    const response = await api.patch(ENDPOINTS.AUTH.PROFILE, profileData);
    return response.data;
  } catch (error) {
    logError('Update profile', error);
    throw formatApiError(error);
  }
};

/**
 * Fetch all profiles belonging to the authenticated user's company.
 * Used by the customer Profile page to display a read-only team member list.
 * @returns {Promise<Array>} - Array of { id, first_name, last_name, email }
 */
export const getCompanyMembers = async () => {
  try {
    const response = await api.get(ENDPOINTS.AUTH.COMPANY_USERS);
    return Array.isArray(response.data) ? response.data : (response.data.results ?? []);
  } catch (error) {
    logError('Get company members', error);
    throw formatApiError(error);
  }
};

export const inviteTeamMember = async ({ first_name, last_name, email }) => {
  try {
    const response = await api.post(ENDPOINTS.AUTH.COMPANY_USERS, { first_name, last_name, email });
    return response.data;
  } catch (error) {
    logError('Invite team member', error);
    throw formatApiError(error);
  }
};

export const getBillingContacts = async () => {
  try {
    const response = await api.get(ENDPOINTS.BILLING_CONTACTS.LIST);
    return Array.isArray(response.data) ? response.data : (response.data.results ?? []);
  } catch (error) {
    logError('Get billing contacts', error);
    throw formatApiError(error);
  }
};

export const addBillingContact = async ({ name, email }) => {
  try {
    const response = await api.post(ENDPOINTS.BILLING_CONTACTS.LIST, { name, email });
    return response.data;
  } catch (error) {
    logError('Add billing contact', error);
    throw formatApiError(error);
  }
};

export const updateBillingContact = async (id, data) => {
  try {
    const response = await api.patch(ENDPOINTS.BILLING_CONTACTS.DETAIL(id), data);
    return response.data;
  } catch (error) {
    logError('Update billing contact', error);
    throw formatApiError(error);
  }
};

export const deleteBillingContact = async (id) => {
  try {
    await api.delete(ENDPOINTS.BILLING_CONTACTS.DETAIL(id));
  } catch (error) {
    logError('Delete billing contact', error);
    throw formatApiError(error);
  }
};

export const setPrimaryBillingContact = async (id) => {
  try {
    const response = await api.post(ENDPOINTS.BILLING_CONTACTS.SET_PRIMARY(id));
    return response.data;
  } catch (error) {
    logError('Set primary billing contact', error);
    throw formatApiError(error);
  }
};
